import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, Button, Stack } from "@mui/material";
import {
  useUsersListQuery,
  type UsersListParams,
  mapUsersFiltersToParams,
  mapUsersSortColumn,
} from "Api/Queries/User/useUsersListQuery";
import { AMLUserListTable } from "Components/AML/AMLUserList/AMLUserListTable";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { useServerTable } from "Hooks/useServerTable";
import { Resources, useResource } from "Translations/Resources";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { useDownloadUsersExportMutation } from "Api/Mutations/User/useDownloadUsersExportMutation";
import { appUrl } from "Utils/UrlUtils";
import type { UserDto } from "Infrastructure/Api/Api";

export const AMLUserListPage = () => {
  const { mutate: downloadUsers, isPending: isDownloadPending } =
    useDownloadUsersExportMutation();
  const { t } = useResource();

  const { queryParams, tableProps } = useServerTable<UserDto>({
    defaultSorting: [{ id: "dateCreated", desc: true }],
  });

  const apiParams = useMemo(
    (): UsersListParams => ({
      offset: queryParams.offset,
      limit: queryParams.limit,
      sortBy: mapUsersSortColumn(queryParams.sortBy),
      sortDirection: queryParams.sortDirection,
      excludedAnonymous: true,
      ...mapUsersFiltersToParams(queryParams.filters),
    }),
    [queryParams],
  );

  const { data, isLoading, isRefetching, isError } =
    useUsersListQuery(apiParams);

  if (isError) {
    return (
      <Layout>
        <ErrorText>{t(Resources.Pages.Content.AmlUsers.Error)}</ErrorText>
      </Layout>
    );
  }

  return (
    <Layout>
      <Button
        variant="outlined"
        color="primary"
        sx={{ maxWidth: "250px" }}
        endIcon={<FileDownloadOutlinedIcon />}
        onClick={() => downloadUsers("aml_export")}
        loading={isDownloadPending}
      >
        {t(Resources.Pages.Content.AmlUsers.ExportButton)}
      </Button>
      <Box display="grid" alignItems="start">
        <AMLUserListTable
          isLoading={isLoading}
          isRefetching={isRefetching}
          data={data?.items || []}
          rowCount={data?.totalCount ?? 0}
          tableProps={tableProps}
        />
      </Box>
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout
      title={t(Resources.Pages.Titles.AmlUsers)}
      backNavigation={{
        mode: "historyStack",
        fallbackUrl: appUrl("dashboard"),
      }}
    >
      <StyledPageWrapper maxWidth="unset !important">
        <Stack gap={4}>{children}</Stack>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default AMLUserListPage;
